
import { supabase, getPublicUrl } from '@/lib/supabase';
import { v4 as uuidv4 } from 'uuid';

const BUCKET_NAME = 'gallery-covers';

export interface GalleryItem {
  id: string;
  title: string;
  author: string;
  image_url: string;
  genre: string;
  rating: number;
  summary: string;
  full_critique?: string;
  suggestions?: string[];
  submitted_date: string;
}

// Share a cover to the gallery (now uses edge function for rate limiting and screening)
export const shareToGallery = async (
  file: File,
  title: string, 
  author: string,
  genre: string,
  rating: number,
  critique: string,
  suggestions?: string[]
): Promise<GalleryItem | null> => {
  try {
    // Convert file to base64
    const reader = new FileReader();
    const base64Promise = new Promise<string>((resolve, reject) => {
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

    const imageBase64 = await base64Promise;

    // Call edge function which handles rate limiting, screening, and storage
    const { data, error } = await supabase.functions.invoke('share-to-gallery', {
      body: {
        imageBase64,
        title,
        author,
        genre,
        rating,
        critique,
        suggestions
      }
    });

    if (error) {
      console.error('Error sharing to gallery:', error);
      throw new Error(error.message || 'Failed to share to gallery');
    }

    if (!data) {
      throw new Error('No data returned from gallery submission');
    }

    return data as GalleryItem;
  } catch (error) {
    console.error('Error sharing to gallery:', error);
    throw error;
  }
};

// Fetch all gallery items
export const fetchGalleryItems = async (): Promise<GalleryItem[]> => {
  try {
    const { data, error } = await supabase
      .from('gallery_items')
      .select('*')
      .order('submitted_date', { ascending: false });
      
    if (error) {
      console.error('Error fetching gallery items:', error);
      return [];
    }
    
    return data as GalleryItem[];
  } catch (error) {
    console.error('Error fetching gallery:', error);
    return [];
  }
};

// Fetch a single gallery item by ID
export const fetchGalleryItemById = async (id: string): Promise<GalleryItem | null> => {
  try {
    const { data, error } = await supabase
      .from('gallery_items')
      .select('*')
      .eq('id', id)
      .single();
      
    if (error) {
      console.error('Error fetching gallery item:', error);
      return null;
    }
    
    return data as GalleryItem;
  } catch (error) {
    console.error('Error fetching gallery item:', error);
    return null;
  }
};

// Delete a gallery item (admin only)
export const deleteGalleryItem = async (id: string, adminKey: string): Promise<boolean> => {
  try {
    const { data, error } = await supabase.functions.invoke('delete-gallery-item', {
      body: { id, adminKey }
    });

    if (error) {
      console.error('Error invoking delete-gallery-item:', error);
      return false;
    }

    return !!data?.success;
  } catch (error) {
    console.error('Error deleting gallery item:', error);
    return false;
  }
};

